import React, { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Btn } from "@/components/common";
import { toast } from "sonner";
import { ChatCircleText, Star } from "@phosphor-icons/react";
import { useModalOpen } from "@/lib/useModalOpen";

const CATS = [{ v: "bug", l: "Bug" }, { v: "idea", l: "Idea" }, { v: "usability", l: "Usability" }, { v: "other", l: "Other" }];

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("usability");
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Out of the way while ANY dialog is open — including this widget's own, whose
  // bottom edge the button otherwise sat against. Same rule as the Ask AI
  // button next to it, asked in one place. (ZEN-169) Where it sits is decided
  // by the helper bar in Layout.jsx. (ZEN-668, ZEN-753)
  const modalOpen = useModalOpen();
  const reset = () => { setCategory("usability"); setRating(0); setMessage(""); };

  const submit = async () => {
    if (!message.trim()) { toast.error("Please add a short message"); return; }
    setSubmitting(true);
    try {
      await api.post("/feedback", { category, rating: rating || null, message: message.trim(), page: window.location.pathname });
      toast.success("Thanks for the feedback!");
      reset(); setOpen(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setSubmitting(false); }
  };

  return (
    <>
      {!modalOpen && (
        <button onClick={() => setOpen(true)} data-testid="feedback-fab" aria-label="Send feedback"
          className="flex h-10 items-center gap-2 rounded-full bg-[#034AA2] px-4 text-sm font-bold text-white active:scale-95 lg:h-9">
          <ChatCircleText size={18} weight="bold" /> Feedback
        </button>
      )}

      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share feedback</DialogTitle>
            <DialogDescription>Found a bug or have an idea to improve the app? Tell us — it goes to the account owner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">Type</span>
              <div className="flex flex-wrap gap-1.5">
                {CATS.map((c) => (
                  <button key={c.v} onClick={() => setCategory(c.v)} data-testid={`feedback-cat-${c.v}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${category === c.v ? "bg-[#034AA2] text-white" : "bg-[#E9E9EB] text-[#475569] hover:bg-[#dcdce0]"}`}>{c.l}</button>
                ))}
              </div>
            </div>
            <div>
              <span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">Rating (optional)</span>
              <div className="flex gap-1" data-testid="feedback-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setRating(n === rating ? 0 : n)} data-testid={`feedback-star-${n}`} aria-label={`${n} star`}>
                    <Star size={26} weight={n <= rating ? "fill" : "regular"} className={n <= rating ? "text-[#F5A623]" : "text-[#C2C9D1]"} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">Message</span>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="feedback-message"
                placeholder="What's working well, what's confusing, or what would you like to see?"
                className="w-full rounded-md border border-[#C2C9D1] p-2.5 text-sm focus:border-[#034AA2] focus:outline-none" />
            </div>
          </div>
          <DialogFooter>
            <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn enterPrimary variant="green" onClick={submit} disabled={submitting} data-testid="feedback-submit">{submitting ? "Sending…" : "Send feedback"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
